import { useEffect, useState } from 'react';
import { api } from '@/shared/api/client';
import { registerWidget, type WidgetRenderProps, type WidgetConfigProps } from '../registry';
import { DEFAULT_TEMP_UNIT, formatTemp, useTempUnit, useTempUnitDraft } from '../tempUnit';
import { TempUnitToggle } from '../TempUnitToggle';

// Local weather tile — data proxied via /api/v1/weather from the MET Norway
// Locationforecast API (free for commercial use with attribution + an
// identifying User-Agent; attribution shown in the tooltip). Location comes
// from the browser's geolocation permission; declined = a gentle hint.
// Temperatures arrive in Celsius and show in the user's Home unit (tempUnit.ts),
// the same as the greeting bar's.

type Weather = {
  temperature_c: number | null; high_c: number | null; low_c: number | null;
  wind_ms: number | null; humidity: number | null; symbol: string | null;
  place?: string | null; attribution?: string;
};
// A tile saved before the per-user unit may still carry `unit`; it only seeds
// that preference (resolveTempUnit) and is no longer edited here.
type Cfg = { lat?: number; lon?: number; place?: string };
type GeoHit = { label: string; lat: number; lon: number };
const fixedLoc = (c: Cfg | undefined) =>
  c && Number.isFinite(c.lat) && Number.isFinite(c.lon) ? { lat: c.lat as number, lon: c.lon as number } : null;

const CONDITION: Record<string, string> = {
  clearsky: 'Clear', fair: 'Fair', partlycloudy: 'Partly cloudy', cloudy: 'Cloudy',
  fog: 'Fog', rain: 'Rain', lightrain: 'Light rain', heavyrain: 'Heavy rain',
  rainshowers: 'Rain showers', lightrainshowers: 'Light showers', heavyrainshowers: 'Heavy showers',
  drizzle: 'Drizzle', snow: 'Snow', lightsnow: 'Light snow', heavysnow: 'Heavy snow',
  snowshowers: 'Snow showers', sleet: 'Sleet', thunderstorm: 'Thunderstorm',
};
const condText = (symbol: string | null) => {
  if (!symbol) return '';
  const base = symbol.split('_')[0];
  return CONDITION[base] || base.charAt(0).toUpperCase() + base.slice(1);
};
// Minimal inline SVG glyphs per condition family (stroke follows text color).
const G = (path: string) => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: path }} />
);
const SUN = G('<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>');
const CLOUD = G('<path d="M17.5 19a4.5 4.5 0 1 0-.6-8.96A6 6 0 1 0 6 18.7"/>');
const PART = G('<circle cx="8" cy="8" r="3"/><path d="M8 2v1.5M2 8h1.5M3.8 3.8l1 1M17.5 20a4.5 4.5 0 1 0-.6-8.96A6 6 0 0 0 6.7 9.5"/>');
const RAIN = G('<path d="M17.5 15a4.5 4.5 0 1 0-.6-8.96A6 6 0 1 0 6 14.7"/><path d="M8 18v2M12 18v3M16 18v2"/>');
const SNOW = G('<path d="M17.5 15a4.5 4.5 0 1 0-.6-8.96A6 6 0 1 0 6 14.7"/><path d="M8 18.5h.01M12 20.5h.01M16 18.5h.01"/>');
const FOG = G('<path d="M4 15h16M6 18h12M8 21h8"/><path d="M17.5 11a4.5 4.5 0 1 0-.6-8.5A6 6 0 0 0 6.4 6"/>');
const STORM = G('<path d="M17.5 14a4.5 4.5 0 1 0-.6-8.96A6 6 0 1 0 6 13.7"/><path d="M13 13l-2.5 4h3L11 21"/>');
function glyph(symbol: string | null) {
  const base = (symbol || '').split('_')[0];
  if (base.includes('thunder')) return STORM;
  if (base.includes('snow') || base === 'sleet') return SNOW;
  if (base.includes('rain') || base === 'drizzle') return RAIN;
  if (base === 'fog') return FOG;
  if (base === 'cloudy') return CLOUD;
  if (base === 'partlycloudy' || base === 'fair') return PART;
  return SUN;
}

function WeatherRender({ config }: WidgetRenderProps) {
  const unit = useTempUnit() ?? DEFAULT_TEMP_UNIT;
  const [state, setState] = useState<'loading' | 'denied' | 'error' | 'ok'>('loading');
  const [w, setW] = useState<Weather | null>(null);
  const fixed = fixedLoc(config as Cfg);
  const fixedKey = fixed ? `${fixed.lat},${fixed.lon}` : '';
  useEffect(() => {
    let stale = false;
    const load = (lat: number, lon: number) =>
      api.get<Weather>(`/weather?lat=${lat.toFixed(2)}&lon=${lon.toFixed(2)}`)
        .then((d) => { if (!stale) { setW(d); setState('ok'); } })
        .catch(() => { if (!stale) setState('error'); });
    // A configured city wins; otherwise fall back to the browser's location.
    if (fixed) { void load(fixed.lat, fixed.lon); return () => { stale = true; }; }
    if (!('geolocation' in navigator)) { setState('error'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => void load(pos.coords.latitude, pos.coords.longitude),
      () => { if (!stale) setState('denied'); },
      { maximumAge: 30 * 60 * 1000, timeout: 8000 },
    );
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedKey]);

  if (state === 'loading') return <div className="text-sm text-muted">Locating…</div>;
  if (state === 'denied') return <div className="text-sm text-muted">Allow location access in the browser to show local weather.</div>;
  if (state === 'error' || !w) return <div className="text-sm text-muted">Weather is unavailable right now.</div>;

  return (
    <div className="flex flex-col h-full" title={w.attribution || 'Weather data from MET Norway'}>
      <div className="flex items-center gap-3">
        <span className="text-amber-500 select-none" aria-hidden="true">{glyph(w.symbol)}</span>
        <div>
          <div className="text-[1.625rem] font-bold text-primary-text font-numeric leading-tight">{formatTemp(w.temperature_c, unit)}</div>
          <div className="text-[0.75rem] text-secondary">{condText(w.symbol)}{w.place ? ` · ${w.place}` : ''}</div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-4 text-[0.75rem] text-muted font-numeric">
        <span>H {formatTemp(w.high_c, unit)}</span>
        <span>L {formatTemp(w.low_c, unit)}</span>
        {w.humidity != null && <span>{Math.round(w.humidity)}%</span>}
        {w.wind_ms != null && <span>{Math.round(w.wind_ms * 3.6)} km/h</span>}
      </div>
    </div>
  );
}

function WeatherConfig({ config, onChange }: WidgetConfigProps) {
  const cfg = (config || {}) as Cfg;
  const choosingUnit = useTempUnitDraft((s) => s.unit) != null;
  const fixed = fixedLoc(cfg);
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<GeoHit[]>([]);
  const [searching, setSearching] = useState(false);
  // Debounced city search against the server-side Nominatim proxy.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      api.get<GeoHit[]>(`/weather/geocode?q=${encodeURIComponent(q)}`)
        .then(setHits).catch(() => setHits([])).finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [search]);
  const pick = (h: GeoHit) => {
    onChange({ ...(config || {}), lat: h.lat, lon: h.lon, place: h.label.split(',')[0] });
    setSearch(''); setHits([]);
  };
  return (
    <div className="grid gap-2">
      {/* Your own preference, not this tile's: the greeting bar shows it too.
          Not offered while an admin edits a role's layout. */}
      {choosingUnit && (
        <>
          <span className="text-[0.75rem] text-muted">Temperature unit (the greeting uses it too)</span>
          <TempUnitToggle className="justify-self-start" />
        </>
      )}
      <label className="text-[0.75rem] text-muted">Location</label>
      <div className="text-[0.8125rem]">
        {fixed
          ? <span>Pinned to <span className="font-medium">{cfg.place || `${fixed.lat}, ${fixed.lon}`}</span>{' — '}
              <button type="button" className="text-primary hover:underline"
                onClick={() => { const { lat: _a, lon: _b, place: _c, ...rest } = cfg; onChange(rest as Record<string, unknown>); }}>
                use my location instead</button></span>
          : <span className="text-muted">Using your browser's location. Search below to pin a city:</span>}
      </div>
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search a city…"
        className="h-[30px] px-2 rounded-md border border-border bg-surface text-[0.8125rem]" />
      {searching && <div className="text-[0.75rem] text-muted">Searching…</div>}
      {hits.length > 0 && (
        <div className="rounded-md border border-border-light max-h-40 overflow-auto">
          {hits.map((h, i) => (
            <button key={i} type="button" onClick={() => pick(h)}
              className="w-full text-left px-2 py-1 text-[0.8125rem] hover:bg-row-highlight truncate" title={h.label}>{h.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

registerWidget({
  type: 'weather', label: 'Weather', defaultSize: 'S',
  icon: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ),
  accent: '#f59e0b',
  ConfigForm: WeatherConfig, Render: WeatherRender,
});
